/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { domUtils } from '@ba-ui-toolkit/utils';

const SCROLL_DRAG_DISTANCE = 50;
const RESTET_HOVER_BUFFER = 75;
const SCROLL_BUFFER = 15;

export default function scrollableDnD(Comp) {
	class ScrollableDnD extends Component {
		static propTypes = {
			scrollableDnDForwardRef: PropTypes.any,
		};

		static contextTypes = {
			dragDropManager: PropTypes.object,
		};

		componentDidMount() {
			this._node.addEventListener('mousemove', this._handleMouseMove);
		}

		componentWillUnmount() {
			cancelAnimationFrame(this._raf);
		}

		_distanceX = 0;
		_distanceY = 0;

		_handleMouseMove = () => {
			if (this._getPosition() && !this._raf) {
				this._raf = requestAnimationFrame(this._handleMouseOver);
				this._lastRAFTime = null;
			}
		};

		_handleMouseOver = time => {
			const position = this._getPosition();
			const boundingRect = this._node.getBoundingClientRect();
			const rect = {
				top: boundingRect.top - SCROLL_BUFFER,
				bottom: boundingRect.bottom + SCROLL_BUFFER,
				left: boundingRect.left - SCROLL_BUFFER,
				right: boundingRect.right + SCROLL_BUFFER,
			};

			if (!this._lastRAFTime) {
				this._lastRAFTime = time;
				requestAnimationFrame(this._handleMouseOver);
			} else if (position && domUtils.offsetWithinBoundingRect(position, rect)) {
				const dt = (time - this._lastRAFTime) / 100;
				this._lastRAFTime = time;

				const distanceBottom = rect.bottom - position.y;
				const distanceTop = position.y - rect.top;
				const distanceLeft = position.x - rect.left;
				const distanceRight = rect.right - position.x;

				let isScrollingBottom = distanceBottom > 0 && distanceBottom < SCROLL_DRAG_DISTANCE;
				let isScrollingTop = distanceTop > 0 && distanceTop < SCROLL_DRAG_DISTANCE;
				let isScrollingLeft = distanceLeft > 0 && distanceLeft < SCROLL_DRAG_DISTANCE;
				let isScrollingRight = distanceRight > 0 && distanceRight < SCROLL_DRAG_DISTANCE;

				if (isScrollingTop && isScrollingBottom) {
					isScrollingTop = distanceTop > distanceBottom;
					isScrollingBottom = !isScrollingTop;
				}

				if (isScrollingLeft && isScrollingRight) {
					isScrollingLeft = distanceLeft > distanceRight;
					isScrollingRight = !isScrollingLeft;
				}

				let distanceY = 0;

				if (isScrollingBottom) {
					distanceY = Math.max((SCROLL_DRAG_DISTANCE - distanceBottom) * dt, 1);
				} else if (isScrollingTop) {
					distanceY = -Math.max((SCROLL_DRAG_DISTANCE - distanceTop) * dt, 1);
				}

				this._node.scrollTop += distanceY;

				if (distanceY) {
					this._distanceY += Math.abs(distanceY);
				} else {
					this._distanceY = 0;
				}

				let distanceX = 0;

				if (isScrollingLeft) {
					distanceX = -Math.max((SCROLL_DRAG_DISTANCE - distanceLeft) * dt, 1);
				} else if (isScrollingRight) {
					distanceX = Math.max((SCROLL_DRAG_DISTANCE - distanceRight) * dt, 1);
				}

				this._node.scrollLeft += distanceX;

				if (distanceX) {
					this._distanceX += Math.abs(distanceX);
				} else {
					this._distanceX = 0;
				}

				if (
					(isScrollingTop || isScrollingBottom || isScrollingLeft || isScrollingRight) &&
					(this._distanceX > RESTET_HOVER_BUFFER || this._distanceY > RESTET_HOVER_BUFFER)
				) {
					this._resetHover();
				}

				requestAnimationFrame(this._handleMouseOver);
			} else {
				this._distanceX = 0;
				this._distanceY = 0;
				cancelAnimationFrame(this._raf);
				this._raf = null;
			}
		};

		_getPosition = () => this.context.dragDropManager.getMonitor().getClientOffset();

		_resetHover = () =>
			this.context.dragDropManager
				.getActions()
				.hover([], { clientOffset: this._getPosition() });

		_ref = r => {
			const { scrollableDnDForwardRef } = this.props;

			this.wrappedComponent = r;
			this._node = ReactDOM.findDOMNode(r);
			scrollableDnDForwardRef && scrollableDnDForwardRef(r);
		};

		render() {
			const { scrollableDnDForwardRef, ...others } = this.props;

			return <Comp {...others} ref={this._ref} />;
		}
	}

	return React.forwardRef((props, ref) => (
		<ScrollableDnD {...props} scrollableDnDForwardRef={ref} />
	));
}
